import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Quote, Star } from "lucide-react";
import { api } from "@/lib/api";

export default function Testimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      const { data } = await api.get("/testimonials");
      setTestimonials(data || []);
    } catch (e) {
      console.error("Erreur lors du chargement des témoignages", e);
    }
  };

  const total = testimonials.length;
  const perView = 2;
  const maxIndex = Math.max(0, total - perView);

  useEffect(() => {
    if (paused || total === 0) return;
    const id = setInterval(() => {
      setIndex((i) => (i >= maxIndex ? 0 : i + 1));
    }, 5000);
    return () => clearInterval(id);
  }, [paused, maxIndex, total]);

  const prev = () => setIndex((i) => (i <= 0 ? maxIndex : i - 1));
  const next = () => setIndex((i) => (i >= maxIndex ? 0 : i + 1));

  // Don't render if no testimonials
  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section
      id="temoignages"
      className="border-t border-stone-200/70 py-24 lg:py-32"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="mb-14 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
          <div>
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.32em] text-stone-500">Témoignages</p>
            <h2 className="font-display max-w-2xl text-4xl font-light leading-tight tracking-tight text-stone-900 sm:text-5xl">
              Ce qu'en disent <span className="italic">les membres.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="testimonials-prev"
              onClick={prev}
              aria-label="Témoignage précédent"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 text-stone-600 transition-colors hover:border-stone-900 hover:text-stone-900"
            >
              <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            </button>
            <button
              data-testid="testimonials-next"
              onClick={next}
              aria-label="Témoignage suivant"
              className="flex h-11 w-11 items-center justify-center rounded-full border border-stone-200 text-stone-600 transition-colors hover:border-stone-900 hover:text-stone-900"
            >
              <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>

        <div className="overflow-hidden" ref={containerRef}>
          <div
            data-testid="testimonials-track"
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${(index * 100) / perView}%)` }}
          >
            {testimonials.map((t, i) => {
              // Generate initials from client_name
              const initials = t.client_name
                .split(' ')
                .map(n => n[0])
                .join('')
                .toUpperCase()
                .slice(0, 2);

              return (
                <figure
                  key={t.id || i}
                  data-testid={`testimonial-${i}`}
                  className="w-full flex-none pr-6 md:w-1/2"
                >
                  <div className="hover-lift relative flex h-full flex-col rounded-2xl border border-stone-200 bg-white p-10">
                    <Quote className="absolute right-8 top-8 h-8 w-8 text-[#3b82f6]/20" strokeWidth={1} />
                    <div className="mb-6 flex items-center gap-1 text-[#3b82f6]">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star 
                          key={s} 
                          className={`h-4 w-4 ${s <= t.rating ? 'fill-[#3b82f6]' : 'fill-stone-200'}`}
                          strokeWidth={0}
                        />
                      ))}
                    </div>
                    <blockquote className="font-display flex-1 text-xl font-light leading-relaxed text-stone-900">
                      « {t.text} »
                    </blockquote>
                    <figcaption className="mt-8 flex items-center gap-4 border-t border-stone-100 pt-6">
                      <div className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-[#3b82f6] text-sm font-medium text-white">
                        {initials}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-stone-900">{t.client_name}</p>
                      </div>
                    </figcaption>
                  </div>
                </figure>
              );
            })}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2">
          {Array.from({ length: maxIndex + 1 }).map((_, i) => (
            <button
              key={i}
              data-testid={`testimonials-dot-${i}`}
              onClick={() => setIndex(i)}
              aria-label={`Aller au groupe ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                index === i ? "w-8 bg-[#3b82f6]" : "w-1.5 bg-stone-300 hover:bg-stone-400"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
